<template>
  <div class="mgt-10px">
    <v-client-table :data="lignes"
                    :columns="columns"
                    :options="options"
                    ref="tableLigneEcheanceFacturation">
      <template v-slot:payeur="{ row }">
        <div>
          <span class="pdr-2">{{ getPayeur() }}</span>
          <a
            v-if="row.indexation.valeurIndiceActuel"
            @click="showIndexationEcheancier(row)">
            <b-tooltip
              :label="$t('detailCommande.indexation.affichageDonneeIndexation')"
              position="is-right">
              <b-icon type="is-primary" icon="cash-plus" size="small"/>
            </b-tooltip>
          </a>
        </div>
      </template>
      <template v-slot:montant="{ row }" >
        <div class="right">
          {{ formatDevise(row.montant) }}
        </div>
      </template>
      <template v-slot:dateDebut="{ row }" >
        {{ formatDate(row.dateDebut) }}
      </template>
      <template v-slot:dateFin="{ row }" >
        {{ formatDate(row.dateFin) }}
      </template>
      <template v-slot:datePrevisionnelleFacturation="{ row }" >
        {{ formatDate(row.datePrevisionnelleFacturation) }}
      </template>
      <template v-slot:fournisseur>
        <span :class="getFournisseur(reference)
                  === $t('echeancierFacturation.tabs.valDiff') ? 'has-text-danger': ''">
          {{ getFournisseur(reference) }}
        </span>
      </template>
      <template v-slot:aFacturer>
        <span :class="getAFacturer(reference)
                    === $t('echeancierFacturation.tabs.valDiff')? 'has-text-danger' : ''">
          {{ getAFacturer(reference) }}
        </span>
      </template>
      <template v-slot:factureId="{ row }">
        <router-link-blank
              v-if="row.factureId"
              :message="formatDate(row.facture.dateDevisCommande)"
              :to=" {
                      name: routeNameFacture,
                      params: { id: row.factureId}
                    }">
        </router-link-blank>
        <span v-else-if="row.facturationFaite && !row.factureId">
                  {{ $t('echeancierFacturation.indicateurFacturation') }}</span>
      </template>
      <template v-slot:appendBody>
        <tr v-if="multiPayeur">
          <td class="has-text-weight-bold">
            {{ $t('echeancierFacturation.tabsEcheancierLignes.totalPayeur', {
            nomCommercial: payeur.nomCommercial, quotePart: getRepartition(payeur)}) }}
          </td>
          <td class="right has-text-weight-bold">
              <p>{{montantTotal}} / {{payeurMontant}} </p>
          </td>
          <td
            class="has-text-weight-bold"
            :colspan="isNotRegie ? '7' : '5'">
          </td>
        </tr>
        <tr v-if="montantFinal">
          <td class="has-text-weight-bold">
            {{ $t('echeancierFacturation.tabsEcheancierLignes.total') }}
          </td>
          <td class="right has-text-weight-bold">
              <p :class="{ 'has-text-danger': !hasAllMontant && !hasIndexation }">
                {{montantFinal.montantlignes}} / {{montantFinal.montantAll}}
              </p>
          </td>
          <td
            class="has-text-weight-bold"
            :colspan="isNotRegie ? '7' : '5'">
            <p v-if="!hasAllMontant && !hasIndexation" class="has-text-danger">
                {{$t('echeancierFacturation.messages.errors.totalDifferent')}}
            </p>
          </td>
        </tr>
      </template>
    </v-client-table>
    <b-modal
      v-model="popUpIndexationEch"
      class="modal"
      has-modal-card
      @close="popUpIndexationEch = false">
      <pop-up-indexation-echeancier
        v-if="lineSelected"
        :echeancier="lineSelected"
        @close="popUpIndexationEch = false"
      />
    </b-modal>
  </div>
</template>

<script>

import cloneDeep from 'lodash/cloneDeep';
import calculArrondi from '@/services/calcul/calculArrondi';
import routerLinkBlank from '@/components/general/routerLinkBlank';
import formatDate from '@/services/general/formatDate';
import { formatDevise } from '@/services/general/filters/formatDevise';
import { columnsSplice } from '@/services/general/columns';
import {
  POURCENTAGE,
  SENS_ACHAT,
  MODULE_ACHAT,
  TYPE_COMMANDE, MODULE_BAIL,
} from '@/configuration/devisConf';
import { NO_PER_PAGE_VALUE, SORT_ICON, TEXTS } from '@/configuration/clientTableConf';
import popUpIndexationEcheancier from '@/components/bodac/dcf/indexation/indexationEcheancierPopUp';

export default {
  props: {
    lignes: {
      type: Array,
      required: true,
    },
    reference: {
      type: String,
      required: true,
    },
    lignesWithReference: {
      type: Array,
      required: true,
    },
    isNotRegie: {
      type: Boolean,
      required: true,
    },
    payeur: {
      type: Object,
      required: true,
    },
    repartition: {
      type: Number,
      required: true,
    },
    multiPayeur: {
      type: Boolean,
      detault: false,
    },
    montantFinal: {
      type: Object,
      required: false,
      default: null,
    },
    dcf: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      formatDate,
      formatDevise,
      lineSelected: null,
      popUpIndexationEch: false,
    };
  },
  components: {
    routerLinkBlank,
    popUpIndexationEcheancier,
  },
  computed: {
    columns() {
      const columns = ['payeur', 'montant', 'dateDebut', 'dateFin', 'datePrevisionnelleFacturation', 'fournisseur', 'aFacturer', 'commentaireFacturation', 'factureId'];
      if (!this.isNotRegie) {
        columnsSplice(columns, 'fournisseur');
        columnsSplice(columns, 'aFacturer');
      }
      return columns;
    },
    options() {
      const texts = cloneDeep(TEXTS);
      texts.count = '';
      return {
        sortIcon: SORT_ICON,
        headings: {
          payeur: this.$t('echeancierFacturation.tabsEcheancierLignes.payeur'),
          montant: this.$t('echeancierFacturation.tabsEcheancierLignes.montant'),
          dateDebut: this.$t('echeancierFacturation.tabsEcheancierLignes.dateDebut'),
          dateFin: this.$t('echeancierFacturation.tabsEcheancierLignes.dateFin'),
          datePrevisionnelleFacturation: this.$t('echeancierFacturation.tabsEcheancierLignes.aFacturer'),
          factureId: this.$t('echeancierFacturation.tabsEcheancierLignes.facturer'),
          commentaireFacturation: this.$t('echeancierFacturation.tabsEcheancierLignes.commentaire'),
          fournisseur: this.$t('detail.schedule.table.fournisseur'),
          aFacturer: this.$t('detail.schedule.table.aFacturer'),
        },
        texts: texts,
        filterable: false,
        perPage: 200,
        perPageValues: NO_PER_PAGE_VALUE,
        pagination: {
          show: false,
        },
        columnsClasses: {
          payeur: 'width-340',
          montant: 'width-205px',
          dateDebut: 'width-205px',
          dateFin: 'width-205px',
          datePrevisionnelleFacturation: 'width-205px',
          factureId: 'width-205px',
          fournisseur: 'width-340',
          commentaireFacturation: 'width-340',
          aFacture: 'width-205px',
        },
        sortable: ['montant', 'dateDebut', 'dateFin', 'datePrevisionnelleFacturation', 'fournisseur', 'aFacturer', 'commentaireFacturation', 'factureId'],
      };
    },
    montantTotal() {
      return this.formatDevise(this.lignes.reduce((a, l) => a + l.montant, 0));
    },
    payeurMontant() {
      return this.formatDevise(this.getMontantEcheancier() * this.repartition / 100);
    },
    hasIndexation() {
      return this.lignes.some(ligne => ligne.indexation?.pourcentageReel !== null);
    },
    hasAllMontant() {
      return this.payeurMontant === this.montantTotal;
    },
    isCommandeAchat() {
      return this.dcf.sens === SENS_ACHAT
        && this.dcf.module === MODULE_ACHAT
        && this.dcf.type === TYPE_COMMANDE;
    },
    isCommandeAchatBailleur() {
      return this.dcf.sens === SENS_ACHAT
        && this.dcf.module === MODULE_BAIL
        && this.dcf.type === TYPE_COMMANDE;
    },
    routeNameFacture() {
      if (this.isCommandeAchatBailleur) {
        return 'c.baux.facture_achat';
      }
      return this.isCommandeAchat ? 'c.achat.factures' : 'c.bodac.factures';
    },
  },
  methods: {
    getRepartition(payeur) {
      return `${payeur.repartition} ${payeur.typeRepartition === POURCENTAGE ? '%' : '€'}`;
    },
    getMontantEcheancier() {
      const total = this.lignesWithReference
        .map((ligne) => { return ligne.totalHtAvecLigneRemise; })
        .reduce((montantTotal, montant) => montantTotal + montant);
      return calculArrondi(total);
    },
    getFournisseur() {
      if (!this.lignesWithReference.every((ligne) => {
        return ligne.fournisseurId === this.lignesWithReference[0].fournisseurId;
      })) return this.$i18n.t('detail.schedule.table.valDiff');
      return this.lignesWithReference[0].fournisseur ? this.lignesWithReference[0].fournisseur.nomCommercial : '';
    },
    getAFacturer() {
      const aFacturerBase = this.lignesWithReference[0].aFacturer;
      if (!this.lignesWithReference.every((ligne) => {
        return ligne.aFacturer === aFacturerBase;
      })) return this.$i18n.t('detail.schedule.table.valDiff');
      return aFacturerBase ? this.$i18n.t('general.oui') : this.$i18n.t('general.non');
    },
    getPayeur() {
      return this.payeur.nomCommercial;
    },
    showIndexationEcheancier(ligne) {
      this.lineSelected = ligne;
      this.popUpIndexationEch = true;
    },
  },
};
</script>
